// src/Admin/Admin.jsx — login gate + admin shell (sidebar, topbar, sections)

import React, { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../Firebase/firebaseConfig';
import logo from '../Assets/logo.png';
import Login from './Login/Login';
import Dashboard from './Dashboard';
import Analytics from '../Pages/Analytics/Analytics';
import CourseManagement from './CourseManagement';
import BlogManagement from './BlogManagement';
import MessagesManagement from './MessagesManagement';
import DiplomaManagement from './DiplomaManagement';
import EnrollManagement from './EnrollManagement';
import Library from './LibraryManageemnt';
import { enrollmentStatus, messageStatus } from './utils';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './admin.css';

const SECTIONS = [
  { id: 'dashboard', title: 'Dashboard', icon: 'fa-gauge-high', description: 'Overview of the whole site' },
  { id: 'analytics', title: 'Analytics', icon: 'fa-chart-column', description: 'Charts and trends' },
  { id: 'courses', title: 'Courses', icon: 'fa-book-open', description: 'Create and edit courses' },
  { id: 'diplomas', title: 'Diplomas', icon: 'fa-graduation-cap', description: 'Create and edit diplomas' },
  { id: 'blogs', title: 'Blog', icon: 'fa-newspaper', description: 'News and events' },
  { id: 'library', title: 'Library', icon: 'fa-book', description: 'Publications and books' },
  { id: 'enrollments', title: 'Enrollments', icon: 'fa-user-graduate', description: 'Applicants and program status' },
  { id: 'messages', title: 'Messages', icon: 'fa-envelope', description: 'Contact form messages' },
];

const initialSection = () => {
  const hash = window.location.hash.replace('#', '');
  return SECTIONS.some((s) => s.id === hash) ? hash : 'dashboard';
};

const Admin = () => {
  const [user, setUser] = useState(undefined); // undefined = still checking, null = signed out
  const [activeSection, setActiveSection] = useState(initialSection);
  const [navOpen, setNavOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dataError, setDataError] = useState('');

  // Auth state
  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  // Live feeds (only while signed in) — power the badges, dashboard and the two inbox screens
  useEffect(() => {
    if (!user) return undefined;
    setDataError('');
    const onError = (err) => {
      console.error('Admin data error:', err);
      setDataError(
        err.code === 'permission-denied'
          ? 'Some data could not be loaded: Firestore rules do not allow this account to read it.'
          : 'Some data could not be loaded. Please refresh.'
      );
    };
    const unsubE = onSnapshot(collection(db, 'enrollments'), (s) => setEnrollments(s.docs.map((d) => ({ id: d.id, ...d.data() }))), onError);
    const unsubM = onSnapshot(collection(db, 'contactMessages'), (s) => setMessages(s.docs.map((d) => ({ id: d.id, ...d.data() }))), onError);
    return () => {
      unsubE();
      unsubM();
    };
  }, [user]);

  const goTo = (id) => {
    setActiveSection(id);
    setNavOpen(false);
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top: 0 });
  };

  const badges = useMemo(
    () => ({
      enrollments: enrollments.filter((e) => enrollmentStatus(e) === 'new').length,
      messages: messages.filter((m) => messageStatus(m) === 'new').length,
    }),
    [enrollments, messages]
  );

  if (user === undefined) {
    return (
      <div className="adm adm-splash">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }

  if (!user) return <Login />;

  const current = SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0];

  const renderSection = () => {
    switch (current.id) {
      case 'dashboard':
        return <Dashboard enrollments={enrollments} messages={messages} onNavigate={goTo} />;
      case 'analytics':
        return <Analytics />;
      case 'courses':
        return <CourseManagement />;
      case 'diplomas':
        return <DiplomaManagement />;
      case 'blogs':
        return <BlogManagement />;
      case 'library':
        return <Library />;
      case 'enrollments':
        return <EnrollManagement enrollments={enrollments} />;
      case 'messages':
        return <MessagesManagement messages={messages} />;
      default:
        return null;
    }
  };

  return (
    <div className="adm">
      {navOpen && <div className="adm-overlay" onClick={() => setNavOpen(false)} />}

      <aside className={`adm-sidebar ${navOpen ? 'is-open' : ''}`} aria-label="Admin navigation">
        <div className="adm-sidebar__brand">
          <img src={logo} alt="CCF" />
          <span>Admin Console</span>
        </div>

        <nav className="adm-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`adm-nav__item ${current.id === s.id ? 'is-active' : ''}`}
              onClick={() => goTo(s.id)}
              aria-current={current.id === s.id ? 'page' : undefined}
            >
              <i className={`fas ${s.icon}`}></i>
              <span>{s.title}</span>
              {badges[s.id] > 0 && <span className="adm-nav__badge">{badges[s.id]}</span>}
            </button>
          ))}
        </nav>

        <div className="adm-sidebar__foot">
          <a href="/" target="_blank" rel="noopener noreferrer" className="adm-sidebar__site">
            <i className="fas fa-arrow-up-right-from-square"></i> View website
          </a>
        </div>
      </aside>

      <div className="adm-main">
        <header className="adm-topbar">
          <button className="adm-topbar__menu" onClick={() => setNavOpen(true)} aria-label="Open menu">
            <i className="fas fa-bars"></i>
          </button>
          <div className="adm-topbar__title">
            <h1>{current.title}</h1>
            <p>{current.description}</p>
          </div>
          <div className="adm-topbar__user">
            <span className="adm-topbar__email" title={user.email}>
              <i className="far fa-user"></i> {user.email}
            </span>
            <button className="adm-btn adm-btn--ghost" onClick={() => signOut(auth)}>
              <i className="fas fa-right-from-bracket"></i> <span>Logout</span>
            </button>
          </div>
        </header>

        <main className="adm-content">
          {dataError && <div className="adm-alert adm-alert--warn" role="alert"><i className="fas fa-triangle-exclamation"></i> {dataError}</div>}
          {renderSection()}
        </main>
      </div>
    </div>
  );
};

export default Admin;
